import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, X, Star } from "lucide-react";
import { api, resolveImage, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/i18n";

export function ImageManager({ images = [], onChange, testid = "img-manager" }) {
  const { t } = useLang();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const data = new FormData();
        data.append("file", file);
        const res = await api.post("/upload", data, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({ id: crypto.randomUUID(), image_url: res.data.image_url, is_primary: false });
      }
      const next = [...images, ...uploaded];
      if (next.length && !next.some((im) => im.is_primary)) next[0].is_primary = true;
      onChange(next);
      toast.success(t("toast.photoUploaded"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("toast.uploadFail"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const setPrimary = (id) => onChange(images.map((im) => ({ ...im, is_primary: im.id === id })));
  const remove = (id) => {
    const next = images.filter((im) => im.id !== id);
    if (next.length && !next.some((im) => im.is_primary)) next[0].is_primary = true;
    onChange(next);
  };

  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap gap-3">
        {images.map((im) => (
          <div key={im.id} data-testid={`${testid}-item-${im.id}`} className={`relative h-24 w-24 rounded-sm overflow-hidden border-2 ${im.is_primary ? "border-sage" : "border-border"}`}>
            <img src={resolveImage(im.image_url)} alt="" className="h-full w-full object-cover" />
            <button type="button" onClick={() => remove(im.id)} data-testid={`${testid}-remove-${im.id}`} className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-destructive transition-colors duration-200"><X className="h-3 w-3" /></button>
            <button type="button" onClick={() => setPrimary(im.id)} data-testid={`${testid}-primary-${im.id}`} title={t("img.setPrimary")} className={`absolute bottom-1 left-1 inline-flex items-center gap-1 px-1.5 h-5 rounded-full text-[10px] font-bold transition-colors duration-200 ${im.is_primary ? "bg-sage text-white" : "bg-white/85 text-gray-600 hover:bg-white"}`}>
              <Star className="h-3 w-3" fill={im.is_primary ? "currentColor" : "none"} /> {im.is_primary ? t("img.primary") : t("img.makePrimary")}
            </button>
          </div>
        ))}
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`${testid}-upload`} className="h-24 w-24 rounded-sm border-2 border-dashed border-border flex flex-col items-center justify-center text-gray-400 hover:border-sage hover:text-sage-dark transition-colors duration-200">
          {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
          <span className="text-[10px] mt-1">{t("img.add")}</span>
        </button>
      </div>
      <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" data-testid={`${testid}-input`} />
    </div>
  );
}
